import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { v4 as uuidv4 } from 'uuid';

import { TBlockVersionMode, TPlacedBlock } from '../../../types';
import Component from './Component';
import { BLOCK_NODE_NAME } from './constants';
import { markInserted } from '../../../hooks/useNodeRegistration.hook';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    'reusable-block': {
      insertBlock: (itemId: string, versionMode: TBlockVersionMode, versionId?: string) => ReturnType;
    };
  }
}

const BlockNode = Node.create({
  name: BLOCK_NODE_NAME,

  inline: false,
  group: 'block fieldBlock',
  atom: true,

  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      item: {
        // we decided to use 'item' instead of 'itemId' to align the attributes with the PlacedBlock schema type
        default: null,
        parseHTML: (element) => element.getAttribute('data-item'),
        renderHTML: (attributes) => ({ 'data-item': attributes.item }),
      },
      itemType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-item-type'),
        renderHTML: (attributes) => ({ 'data-item-type': attributes.itemType }),
      },
      versionMode: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-version-mode'),
        renderHTML: (attributes) => ({ 'data-version-mode': attributes.versionMode }),
      },
      versionId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-version-id'),
        renderHTML: (attributes) => ({ 'data-version-id': attributes.versionId }),
      },
      loadedId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-loaded-id'),
        renderHTML: (attributes) => ({ 'data-loaded-id': attributes.loadedId }),
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['reusable-block', HTMLAttributes];
  },

  parseHTML() {
    return [
      {
        tag: 'reusable-block',
      },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component, { trackNodeViewPosition: true });
  },

  addCommands() {
    return {
      // ...this.parent?.(),
      insertBlock:
        (
          itemId,
          versionMode,
          versionId // function type is declared at the top of this file
        ) =>
        ({ commands }) => {
          const attrs: TPlacedBlock = {
            id: uuidv4(),
            item: itemId,
            itemType: 'DOCUMENT',
            versionMode,
            versionId,
          };
          markInserted(attrs.id);
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },
});

export default BlockNode;
